import React, { createContext, useContext, useReducer, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";

interface TourState {
  run: boolean;
  stepIndex: number;
  tourType: "onboarding" | "page-help" | null;
}

type TourAction =
  | { type: "START_ONBOARDING" }
  | { type: "START_PAGE_HELP" }
  | { type: "STOP" }
  | { type: "SET_STEP"; index: number }
  | { type: "NEXT_STEP" }
  | { type: "CONTINUE_ON_PAGE" };

const initialState: TourState = {
  run: false,
  stepIndex: 0,
  tourType: null,
};

function tourReducer(state: TourState, action: TourAction): TourState {
  console.log(`%c[TOUR CONTEXT] Action: ${action.type}`, "background: #222; color: #bada55; padding: 2px 4px; border-radius: 4px;");
  switch (action.type) {
    case "START_ONBOARDING":
      return { run: true, stepIndex: 0, tourType: "onboarding" };
    case "START_PAGE_HELP":
      return { run: true, stepIndex: 0, tourType: "page-help" };
    case "STOP":
      return { run: false, stepIndex: 0, tourType: null };
    case "SET_STEP":
      return { ...state, stepIndex: action.index };
    case "NEXT_STEP":
      return { ...state, stepIndex: state.stepIndex + 1 };
    case "CONTINUE_ON_PAGE":
      return { ...state, stepIndex: 0 };
    default:
      return state;
  }
}

interface TourContextType {
  run: boolean;
  stepIndex: number;
  tourType: "onboarding" | "page-help" | null;
  startOnboarding: (targetPath?: string) => void;
  startPageHelp: () => void;
  stopTour: () => void;
  setStepIndex: (index: number) => void;
  nextStep: () => void;
  continueOnPage: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(tourReducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();

  const startOnboarding = useCallback((targetPath?: string) => {
    dispatch({ type: "START_ONBOARDING" });
    const destination = targetPath || "/dashboard";
    if (location.pathname !== destination) {
      navigate(destination);
    }
  }, [location.pathname, navigate]);

  const startPageHelp = useCallback(() => {
    dispatch({ type: "START_PAGE_HELP" });
  }, []);

  const stopTour = useCallback(() => {
    dispatch({ type: "STOP" });
  }, []);

  const setStepIndex = useCallback((index: number) => {
    dispatch({ type: "SET_STEP", index });
  }, []);

  const nextStep = useCallback(() => {
    dispatch({ type: "NEXT_STEP" });
  }, []);

  const continueOnPage = useCallback(() => {
    dispatch({ type: "CONTINUE_ON_PAGE" });
  }, []);

  // Stop page-help tour on navigation
  useEffect(() => {
    if (state.tourType === "page-help") {
      dispatch({ type: "STOP" });
    }
  }, [location.pathname]);

  return (
    <TourContext.Provider value={{
      run: state.run,
      stepIndex: state.stepIndex,
      tourType: state.tourType,
      startOnboarding,
      startPageHelp,
      stopTour,
      setStepIndex,
      nextStep,
      continueOnPage,
    }}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) throw new Error("useTour must be used within a TourProvider");
  return context;
};
